<template>
  <BottomWrapper
    id="ticket-confirmation-wrapper"
    :buttons="buttons"
    :show-back="false"
    cancelable-request
    @select="handleAction"
    @support-request:cancel="$emit('support-request:cancel', $event)"
  >
    <div class="confirmation-container">
      <div class="success-icon">
        <CheckIcon />
      </div>

      <div class="center title-wrapper">
        <div>{{ $t('page.confirmation.ticket_created.title') }}</div>
      </div>

      <div class="ticket-info">
        <div class="ticket-id">
          <span class="label">{{ $t('page.confirmation.ticket_created.ticket_id') }}:</span>
          <span class="value">{{ ticketId }}</span>
        </div>
      </div>

      <div class="confirmation-message">
        <p>{{ $t('page.confirmation.ticket_created.message', { email: email }) }}</p>
      </div>

      <div class="next-steps">
        <h4>{{ $t('page.confirmation.ticket_created.next_steps') }}</h4>
        <ul>
          <li><CheckIcon class="check-icon" />{{ $t('page.confirmation.ticket_created.step_1') }}</li>
          <li><CheckIcon class="check-icon" />{{ $t('page.confirmation.ticket_created.step_2') }}</li>
          <li><CheckIcon class="check-icon" />{{ $t('page.confirmation.ticket_created.step_3') }}</li>
        </ul>
      </div>
    </div>
  </BottomWrapper>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import BottomWrapper from '@/views/Chatroom/BottomWrapper.vue';
import CheckIcon from '@/components/icons/CheckIcon.vue';
import * as Locale from '@/plugins/i18n';
import { analytics } from '@/common/logs';
import { PAGE_EVENTS, BTN_EVTS } from '@/common/logs/events';

@Component({
  components: {
    BottomWrapper,
    CheckIcon,
  },
})
export default class TicketConfirmation extends Vue {
  @Prop({ required: true }) readonly ticketId!: string;
  @Prop({ required: true }) readonly email!: string;

  readonly Locale = Locale;

  get buttons() {
    return [
      {
        name: this.$t('page.confirmation.ticket_created.close'),
        id: 'close',
        isActive: true,
      },
    ];
  }

  created() {
    document.title = 'Ticket Created';

    // Track ticket confirmation page view
    analytics.trackPageEvent({
      name: PAGE_EVENTS.GUEST_TICKET_CONFIRMATION,
      enter: true,
      metadata: {
        ticketId: this.ticketId,
        email: this.email,
      },
    });
  }

  handleAction({ id }: { id: string }) {
    if (id === 'close') {
      // Track close button click
      analytics.trackClick({
        name: BTN_EVTS.GUEST_TICKET_CONFIRMATION_CLOSE,
        metadata: {
          ticketId: this.ticketId,
          email: this.email,
        },
      });

      this.$emit('close');
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/base.scss';
.confirmation-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 0;
  margin: 0 10px;
  text-align: center;
}

.success-icon {
  width: 6rem;
  height: 6rem;
  background: $guest-otp-success-color;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 3rem;
}

.title-wrapper {
  font-size: 2rem;
  font-weight: 600;
  color: $guest-otp-text-color;
}

.ticket-info {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  min-width: 200px;
}

.ticket-id {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .label {
    font-size: 1.3rem;
    color: #6b7280;
    font-weight: 500;
  }

  .value {
    font-size: 1.8rem;
    font-weight: 700;
    color: $guest-otp-text-color;
    letter-spacing: 0.1rem;
  }
}

.confirmation-message {
  color: $guest-otp-text-color;
  font-size: 1.4rem;
  line-height: 1.6;
  max-width: 400px;
}

.next-steps {
  text-align: left;
  max-width: 400px;
  width: 100%;

  h4 {
    font-size: 1.5rem;
    font-weight: 600;
    color: $guest-otp-text-color;
    margin-bottom: 1rem;
    text-align: center;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    display: flex;
    align-items: center;
    margin-bottom: 0.8rem;
    font-size: 1.3rem;
    line-height: 1.5;
    color: $guest-otp-text-color;

    .check-icon {
      color: $guest-otp-success-color;
      margin-right: 0.8rem;
      flex-shrink: 0;
    }
  }
}
</style>
